/**
 * UX-spec §9.8 — ONE accessible-name/label scheme for the BOM tree, closing
 * Findings 4 and 7 and reframing R7's copy, all from the SAME two pure
 * functions rather than three separately-drifting inline string templates
 * (§9.8.0: that drift is exactly how Findings 4 and 7 survived one fix round
 * already). Both are pure functions of `ancestorNames: readonly string[]`
 * (immediate parent first, outward — the shape already documented at
 * `StagingBomLeafRow.tsx`/`StagingBomPartsTree.tsx`). No new prop, no new
 * field, no contract change (§9.8.4).
 */

/**
 * §9.8.1(A) — the LEAF suffix, full ancestor chain. This is the single
 * `, in ${...}` template in the module: {@link buildLeafAccessibleName}
 * composes this function for its `occurrenceCount <= 1` case (which covers
 * both the resolved leaf's checkbox/Remove labels and the unresolved leaf's
 * "Nothing staged for" label — none of them build the suffix inline). Kept
 * as its own exported function, rather than inlined into
 * {@link buildLeafAccessibleName}, so the suffix template can be unit-tested
 * independently of the action-verb wrapper around it.
 */
export function buildAssemblyPathSuffix(ancestorNames: readonly string[]): string {
  return ancestorNames.length > 0 ? `, in ${ancestorNames.join(', ')}` : ''
}

/**
 * §9.8.2 items 1–3 — the ONE leaf accessible-name template, covering:
 *  - a resolved leaf's checkbox (`action: 'Select'`) and Remove button
 *    (`action: 'Remove'`), occurrence count possibly > 1;
 *  - the unresolved leaf's `aria-label` (`action: 'Nothing staged for'`),
 *    `occurrenceCount` always `1` — an unresolved leaf is never deduped
 *    (D17.1), so the multiplicity clause never applies to it and this
 *    reduces to the plain `${action} ${name}${suffix}` shape item 3 asks
 *    for.
 *
 * `occurrenceCount > 1` inverts which clause is grammatically primary
 * (§9.8.2 item 2): "referenced at N positions… including X" states X as one
 * true instance of a stated total, rather than "in X" asserting X as the
 * complete, single containing location — the exact axis WCAG 4.1.2 (Name,
 * Role, Value) cares about once a leaf has more than one occurrence.
 */
export function buildLeafAccessibleName(
  action: string,
  name: string,
  ancestorNames: readonly string[],
  occurrenceCount: number,
): string {
  if (occurrenceCount > 1) {
    const assemblyPath = ancestorNames.length > 0 ? ancestorNames.join(', ') : null
    return assemblyPath
      ? `${action} ${name}, referenced at ${occurrenceCount} positions in this BOM, including ${assemblyPath}`
      : `${action} ${name}, referenced at ${occurrenceCount} positions in this BOM`
  }
  return `${action} ${name}${buildAssemblyPathSuffix(ancestorNames)}`
}

/**
 * §9.8.1(B)/§9.8.2 item 4 — the GROUP row's own accessible name, immediate-
 * parent-only (never the full chain (A) uses) so a group row's label doesn't
 * compound in length at every nesting level on the way down a deep tree
 * (§9.8.1's stated reason). Used ONLY by a group row's own label — never by
 * its children's `childAncestorNames`, which stay the full chain.
 */
export function buildGroupAccessibleName(name: string, ancestorNames: readonly string[]): string {
  return ancestorNames.length > 0
    ? `Select all parts in ${name}, under ${ancestorNames[0]}`
    : `Select all parts in ${name}`
}

/**
 * ADR-0157 — the ONE "how do we name a BOM line with no reliable line
 * number" fallback, extracted from `StagingBomLeafRow`'s unresolved branch
 * (Gate-5 re-review residual) so both that row AND `StagingPage`'s
 * page-level `aria-live` Dismiss announcement build the IDENTICAL label
 * rather than two copies drifting apart. The customer's own `bomLine` wins
 * when present; else this leaf's 0-based `siblingIndex` among its direct
 * siblings, rendered as a 1-based ordinal — never the bare string "BOM
 * line" with nothing after it (see the row's own doc comment for why that
 * string alone is a live WCAG 4.1.2 collision).
 */
export function formatBomLineLabel(bomLine: number | null, siblingIndex: number): string {
  return bomLine !== null ? `BOM line ${bomLine}` : `BOM line item ${siblingIndex + 1}`
}

/**
 * ADR-0155 WARNING F (Gate-5 re-review) — the ONE source of truth for the
 * four section labels `StagingPage.tsx` both HEADS each section with
 * (`staging-needs-resolution-heading`/`staging-bom-parts-heading`/
 * `staging-identified-heading`/`staging-matched-heading`) and NAMES inside
 * its consolidated search-disclosure announcement
 * (`zeroMatchSectionLabels`/`searchDisclosureMessage`). Before this constant
 * existed, the two were four independently hardcoded string literals each —
 * the heading rendered live, the announcement built once at module scope —
 * so renaming a heading silently left the announcement naming a section that
 * no longer exists, with no test and no type error to catch it. Both call
 * sites now read the SAME four strings; there is no longer a second place to
 * remember to update.
 *
 * Not BOM-tree-specific like the rest of this module (these are the page's
 * flat/unified SECTION labels, not a leaf/group accessible name), but this
 * file is Staging's one existing home for "the accessible name a section of
 * this page is known by" — see `staging-part-search.test.ts`'s / this
 * module's own doc comments for the "one shared source, not a third inline
 * template" pattern this repo already prefers for exactly this class of bug.
 */
export const STAGING_SECTION_LABELS: {
  readonly needsResolution: string
  readonly bomParts: string
  readonly identified: string
  readonly matched: string
} = {
  needsResolution: 'Needs resolution',
  bomParts: 'BOM parts',
  identified: 'Identified parts',
  matched: 'Matched to catalog',
}
